import { useEffect, useState } from 'react'

import { useI18n } from '@/locales/client'
import { checkPromotion, type PromotionData } from '@/services/promotion'
import { $country } from '@/stores/Country'
import { $language } from '@/stores/Language'
import { $promotion, $promotionLoading } from '@/stores/PromotionStore'
import { useStore } from '@nanostores/react'

interface TimeLeft {
  days: number
  hours: number
  minutes: number
}

function useCountdown(endDate: string): TimeLeft | null {
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(null)

  useEffect(() => {
    function calculateTimeLeft(): TimeLeft | null {
      const end = new Date(endDate).getTime()
      const now = new Date().getTime()
      const difference = end - now

      if (difference <= 0) {
        return null
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
      }
    }

    setTimeLeft(calculateTimeLeft())

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft())
    }, 60000) // Update every minute

    return () => clearInterval(timer)
  }, [endDate])

  return timeLeft
}

export default function PromotionBanner() {
  const promotion = useStore($promotion)
  const country = useStore($country)
  const language = useStore($language)
  const timeLeft = useCountdown(promotion?.endingDatetime ?? '')
  const t = useI18n()

  useEffect(() => {
    async function fetchPromotion() {
      $promotionLoading.set(true)
      const countryCode = country || 'DE'
      const result = await checkPromotion(countryCode, language)
      
      if (result?.promotionAvailable && result.promotionData && 'id' in result.promotionData) {
        $promotion.set(result.promotionData as PromotionData)
      } else {
        $promotion.set(null)
      }
      $promotionLoading.set(false)
    }

    fetchPromotion()
  }, [country, language])

  if (!promotion) {
    return null
  }

  return (
    <div className='p-4 lg:p-0'>
    <div className="relative w-full rounded-[4px] lg:rounded-[0px] overflow-hidden">
      {promotion.imagesBackgroundImageS3Path && (
        <img
          src={promotion.imagesBackgroundImageS3Path}
          alt="Promotion Background"
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      <div className="relative z-20 flex flex-col items-center justify-center py-8 text-white">
        {promotion.labelHeadline && (
          <div className="open-sans-bold font-bold text-[14px] center mb2 text-rsfColor1 bg-white rounded-[2px] py-[6px] px-2 lg:mb-2">
            {promotion.labelHeadline}
          </div>
        )}
        {promotion.labelHeadline2 && (
          <h2 className="helvetiva-bold font-bold font-inter lg:text-[2rem] text-[1.5rem] text-white mb-2">
            {promotion.labelHeadline2}
          </h2>
        )}
        {promotion.labelHeadline3 && (
          <p className="open-sans-semibold font-semibold text-white text-[14px] color-white opacity-60">
            {promotion.labelHeadline3}
          </p>
        )}
      </div>
    </div>
    {timeLeft && (
      <div className="mt-3 text-center">
        <span className="text-sm font-semibold text-gray-700">{t('promotionBanner.saleExpiresIn')} </span>
        <span className="text-sm font-bold text-rsfColor1">
          {timeLeft.days > 0 && (
            <>{timeLeft.days} {timeLeft.days === 1 ? t('promotionBanner.daysSingular') : t('promotionBanner.daysPlural')} </>
          )}
          {timeLeft.hours > 0 && (
            <>{timeLeft.hours} {timeLeft.hours === 1 ? t('promotionBanner.hoursSingular') : t('promotionBanner.hoursPlural')} </>
          )}
          {timeLeft.minutes > 0 && (
            <>{timeLeft.minutes} {timeLeft.minutes === 1 ? t('promotionBanner.minutesSingular') : t('promotionBanner.minutesPlural')}</>
          )}
        </span>
      </div>
    )}
    </div>
  )
}
